import { equals } from 'ramda'
import { ref } from 'vue'
import type { Ref } from 'vue'

import { usePolls } from '@/composables/polls'
import {
  POLLS_GROUP_NAME,
  REGISTRATION_POLLS,
} from './consts.ts'

type RegistrationPollId = (typeof REGISTRATION_POLLS)[keyof typeof REGISTRATION_POLLS]

type OnboardingPollChoice = {
  id: number | string
  internal_name: string
}

export type OnboardingPoll = {
  name: string
  savedChoices?: unknown[]
  choices?: OnboardingPollChoice[]
  savedInternalNamesChoices?: string[]
}

const onboardingPolls = ref<OnboardingPoll[] | null>(null)
const isBusinessWithoutStaffers = ref(false)

const REGISTRATION_POLLS_ORDER = [
  REGISTRATION_POLLS.OTHER_APPS,
  REGISTRATION_POLLS.BOOKING_ADVANCE,
  REGISTRATION_POLLS.BUSINESS_FOCUS,
  REGISTRATION_POLLS.NUMBER_OF_STAFFERS,
] as const satisfies readonly RegistrationPollId[]

const registrationPollOrderSet = new Set<string>(REGISTRATION_POLLS_ORDER)

export type UseOnboardingPollsReturn = {
  getPoll: (pollName: string) => OnboardingPoll | undefined
  loadPolls: () => Promise<void>
  onboardingPolls: Ref<OnboardingPoll[] | null>
  submitPoll: (args: { pollName: string; choices: unknown[] }) => Promise<void>
  REGISTRATION_POLLS: typeof REGISTRATION_POLLS
  isBusinessWithoutStaffers: Ref<boolean>
}

export function useOnboardingPolls (): UseOnboardingPollsReturn {
  const { loadPollsList, savePoll } = usePolls()

  const loadPolls = async () => {
    const allPolls = (await loadPollsList(POLLS_GROUP_NAME)) as OnboardingPoll[] | null | undefined
    onboardingPolls.value = allPolls?.filter(poll => registrationPollOrderSet.has(poll.name)) ?? null
  }

  const getPoll = (pollName: string): OnboardingPoll | undefined => {
    return onboardingPolls.value?.find(poll => poll.name === pollName)
  }

  const submitPoll = async ({ pollName, choices }: { pollName: string; choices: unknown[] }) => {
    try {
      const poll = getPoll(pollName)
      if (!poll || equals(poll.savedChoices, choices)) return

      await savePoll({ pollName, choices })
      poll.savedChoices = choices
      const matchedChoices: string[] = []
      choices.forEach(choice => {
        const matchingChoice = poll.choices?.find(item => item.id == choice)
        if (matchingChoice) {
          matchedChoices.push(matchingChoice.internal_name)
        }
      })
      poll.savedInternalNamesChoices = matchedChoices

      if (pollName === REGISTRATION_POLLS.NUMBER_OF_STAFFERS) {
        const CHOICE_JUST_ME_ID = poll.choices?.find(choice => choice.internal_name === 'just_me_2')?.id
        isBusinessWithoutStaffers.value = CHOICE_JUST_ME_ID !== undefined && choices.includes(CHOICE_JUST_ME_ID)
      }
      return Promise.resolve()
    } catch (err: unknown) {
      const response = err && typeof err === 'object' && 'response' in err
        ? (err as { response: { data: unknown } }).response
        : undefined
      return Promise.reject(response?.data)
    }
  }

  return {
    getPoll,
    loadPolls,
    onboardingPolls,
    submitPoll,
    REGISTRATION_POLLS,
    isBusinessWithoutStaffers,
  }
}
